import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Sparkles, ShieldCheck, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { pauseSmoothScroll, resumeSmoothScroll } from "@/components/SmoothScroll";

const HELP_ME_PICK = "Not sure yet — help me pick";
const OWN_IDEA = "My own idea";
const RESEND_COOLDOWN = 30; // seconds
const PHONE_PATTERN = /^\+?[0-9]{7,15}$/;
const OTP_PATTERN = /^[0-9]{4,8}$/;
const isValidPhone = (phone) => PHONE_PATTERN.test(phone.replace(/[\s-]/g, ""));

// Cached across dialog opens/instances so we only ask the backend once per
// page load whether phone OTP is actually configured (Twilio credentials
// set). Undefined = not yet checked, null = check in flight.
let otpStatusCache;

async function getOtpStatus() {
  if (otpStatusCache) return otpStatusCache;
  try {
    const { data } = await api.get("/otp/status");
    otpStatusCache = { enabled: !!data.enabled, whatsappEnabled: !!data.whatsapp_enabled };
  } catch {
    otpStatusCache = { enabled: false, whatsappEnabled: false };
  }
  return otpStatusCache;
}

// `presetInterest` lets a caller (e.g. clicking "Enquire — Option B" in
// ProjectsShowcase) open the dialog with that project already selected.
export default function EnquiryDialog({ open, onClose, course, source = "course", presetInterest = "" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [interest, setInterest] = useState("");
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  // Phone OTP state — inert (otpEnabled stays false) until Twilio is
  // actually configured on the backend, so this whole block of UI simply
  // doesn't render on a fresh install.
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [otpWhatsappEnabled, setOtpWhatsappEnabled] = useState(false);
  const [otpChannel, setOtpChannel] = useState("sms"); // "sms" | "whatsapp" — whichever was last sent
  const [waPendingToken, setWaPendingToken] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpToken, setOtpToken] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const verifiedPhoneRef = useRef("");

  useEffect(() => {
    getOtpStatus().then(({ enabled, whatsappEnabled }) => {
      setOtpEnabled(enabled);
      setOtpWhatsappEnabled(whatsappEnabled);
    });
  }, []);

  // Lock the page behind the modal while it's open — both the Lenis
  // smooth-scroll layer (which otherwise keeps hijacking wheel/touch input
  // for the whole document) and native body scroll, so wheeling or
  // swiping over the dialog scrolls the dialog, not the page behind it.
  useEffect(() => {
    if (!open) return;
    pauseSmoothScroll();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      resumeSmoothScroll();
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Re-sync the picked interest whenever the dialog is (re)opened with a
  // different preset — e.g. the visitor picks a different project card.
  useEffect(() => {
    if (open) setInterest(presetInterest || "");
  }, [open, presetInterest]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // If the visitor edits their phone number after verifying it, the old
  // verification no longer applies to the new number — require re-verify.
  useEffect(() => {
    if (otpVerified && form.phone !== verifiedPhoneRef.current) {
      setOtpVerified(false);
      setOtpToken("");
    }
  }, [form.phone, otpVerified]);

  const otpRequired = otpEnabled || otpWhatsappEnabled;

  const projectChoices = course?.projects?.map((p) => p.title) || [];
  const interestChoices = projectChoices.length > 0 ? [...projectChoices, OWN_IDEA, HELP_ME_PICK] : [];

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updPhone = (e) => {
    const digitsOnly = e.target.value.replace(/[^0-9+\s-]/g, "");
    setForm((f) => ({ ...f, phone: digitsOnly }));
    setPhoneError("");
  };

  const sendCode = async (channel = "sms") => {
    if (!form.phone || cooldown > 0) return;
    if (!isValidPhone(form.phone)) {
      setPhoneError("Enter a valid phone number (digits only, with country code).");
      return;
    }
    setPhoneError("");
    setOtpError("");
    setOtpSending(true);
    try {
      if (channel === "whatsapp") {
        const { data } = await api.post("/otp/send-whatsapp", { phone: form.phone });
        setWaPendingToken(data.pending_token);
      } else {
        await api.post("/otp/send", { phone: form.phone });
      }
      setOtpChannel(channel);
      setOtpSent(true);
      setOtpCode("");
      setCooldown(RESEND_COOLDOWN);
      toast.success("Code sent", {
        description: channel === "whatsapp"
          ? `We sent a WhatsApp code to ${form.phone}.`
          : `We texted a verification code to ${form.phone}.`,
      });
    } catch (err) {
      setOtpError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send a code. Please try again.");
    } finally {
      setOtpSending(false);
    }
  };

  const verifyCode = async () => {
    if (!OTP_PATTERN.test(otpCode)) {
      setOtpError("Enter the code we sent you (4–8 digits).");
      return;
    }
    setOtpError("");
    setOtpVerifying(true);
    try {
      const { data } = otpChannel === "whatsapp"
        ? await api.post("/otp/verify-whatsapp", { phone: form.phone, code: otpCode, pending_token: waPendingToken })
        : await api.post("/otp/verify", { phone: form.phone, code: otpCode });
      setOtpVerified(true);
      setOtpToken(data.token);
      verifiedPhoneRef.current = form.phone;
      toast.success("Phone verified");
    } catch (err) {
      setOtpError(formatApiErrorDetail(err.response?.data?.detail) || "That code didn't match.");
    } finally {
      setOtpVerifying(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isValidPhone(form.phone)) {
      setPhoneError("Enter a valid phone number (digits only, with country code).");
      return;
    }
    setPhoneError("");
    if (otpRequired && !otpVerified) {
      setError("Please verify your phone number before sending your enquiry.");
      return;
    }
    setSubmitting(true);
    try {
      const qaLines = (course?.qualifying_questions || [])
        .filter((q) => answers[q.key])
        .map((q) => `${q.question} ${answers[q.key]}`);
      const composedMessage = [
        interest ? `Interested in: ${interest}` : null,
        ...qaLines,
        form.message || null,
      ]
        .filter(Boolean)
        .join("\n\n");
      await api.post("/enquiries", {
        ...form,
        message: composedMessage,
        course_slug: course?.slug || null,
        course_title: course?.title || null,
        source,
        otp_token: otpRequired ? otpToken : undefined,
      });
      toast.success("Enquiry received", {
        description: "We'll reach out to confirm fee, batch dates and next steps.",
      });
      setForm({ name: "", email: "", phone: "", message: "" });
      setInterest("");
      setAnswers({});
      setPhoneError("");
      setOtpSent(false);
      setOtpVerified(false);
      setOtpToken("");
      setOtpCode("");
      setOtpChannel("sms");
      setWaPendingToken("");
      verifiedPhoneRef.current = "";
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !submitting && (!otpRequired || otpVerified) && isValidPhone(form.phone);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="enquiry-dialog"
          className="fixed inset-0 z-[70] flex items-end md:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-ink/60 backdrop-blur-sm"
            onClick={onClose}
            data-testid="enquiry-backdrop"
          />
          <motion.div
            className="relative w-full md:max-w-lg max-h-[90vh] md:max-h-[85vh] bg-surface rounded-t-3xl md:rounded-xl border border-line shadow-lg overflow-hidden flex flex-col"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="shrink-0 flex items-start justify-between p-6 md:p-8 border-b border-line">
              <div className="pr-6">
                <p className="font-mono text-[10px] uppercase tracking-widest text-terracotta mb-2">
                  Enquire — no payment now
                </p>
                <h3 className="font-display font-semibold tracking-tight text-2xl md:text-3xl leading-tight">
                  {course ? course.title : "Tell us what you want to learn"}
                </h3>
                {course && /\d/.test(course.starting_fee) && (
                  <p className="font-mono text-[11px] uppercase tracking-widest text-emerald mt-3">
                    From {course.starting_fee} · confirmed on enquiry
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                data-testid="enquiry-close"
                className="p-1 hover:opacity-60 transition-opacity"
                aria-label="Close"
              >
                <X size={22} />
              </button>
            </div>

            <form onSubmit={submit} className="p-6 md:p-8 space-y-4 overflow-y-auto" data-lenis-prevent>
              <Field label="Your name" testId="enquiry-name">
                <input
                  required
                  value={form.name}
                  onChange={upd("name")}
                  data-testid="enquiry-name-input"
                  className="w-full bg-white text-ink rounded-lg border border-line px-4 py-3 outline-none focus:border-emerald focus:ring-1 focus:ring-emerald transition-colors"
                  placeholder="Full name"
                />
              </Field>
              {interestChoices.length > 0 && (
                <Field label="Which idea interests you? (optional)" testId="enquiry-interest">
                  <div className="flex flex-wrap gap-2">
                    {interestChoices.map((choice) => {
                      const isPicked = interest === choice;
                      return (
                        <button
                          key={choice}
                          type="button"
                          onClick={() => setInterest(isPicked ? "" : choice)}
                          data-testid={`enquiry-interest-${choice.replace(/\s+/g, "-").toLowerCase()}`}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[13px] font-semibold transition-colors ${
                            isPicked
                              ? "bg-emerald text-white border-emerald"
                              : "border-line text-ink/80 hover:border-emerald"
                          }`}
                        >
                          {isPicked && <Sparkles size={12} />}
                          {choice}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              )}

              {course?.qualifying_questions?.length > 0 && (
                <div className="space-y-4 pt-1 pb-1 border-t border-line/70">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-terracotta pt-4">
                    Help us prepare for your call (optional)
                  </p>
                  {course.qualifying_questions.map((q) => (
                    <Field key={q.key} label={q.question} testId={`enquiry-q-${q.key}`}>
                      <div className="space-y-2">
                        {q.options.map((opt) => {
                          const isPicked = answers[q.key] === opt;
                          return (
                            <RadioCard
                              key={opt}
                              label={opt}
                              selected={isPicked}
                              testId={`enquiry-q-${q.key}-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                              onClick={() =>
                                setAnswers((a) => ({ ...a, [q.key]: isPicked ? undefined : opt }))
                              }
                            />
                          );
                        })}
                      </div>
                    </Field>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Email" testId="enquiry-email">
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={upd("email")}
                    data-testid="enquiry-email-input"
                    className="w-full bg-white text-ink rounded-lg border border-line px-4 py-3 outline-none focus:border-emerald focus:ring-1 focus:ring-emerald transition-colors"
                    placeholder="you@email.com"
                  />
                </Field>
                <Field label="Phone" testId="enquiry-phone">
                  <div className="flex gap-2">
                    <input
                      required
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      pattern="\+?[0-9]{7,15}"
                      value={form.phone}
                      onChange={updPhone}
                      data-testid="enquiry-phone-input"
                      disabled={otpVerified}
                      className="w-full bg-white text-ink rounded-lg border border-line px-4 py-3 outline-none focus:border-emerald focus:ring-1 focus:ring-emerald transition-colors disabled:opacity-60"
                      placeholder="+91 98765 43210"
                    />
                    {!otpVerified && !otpSent && otpEnabled && (
                      <button
                        type="button"
                        onClick={() => sendCode("sms")}
                        disabled={!form.phone || otpSending || cooldown > 0}
                        data-testid="enquiry-otp-send"
                        className="shrink-0 rounded-lg border border-emerald text-emerald font-semibold text-xs px-3 whitespace-nowrap hover:bg-emerald/5 transition-colors disabled:opacity-50 disabled:hover:bg-transparent"
                      >
                        {otpSending && otpChannel === "sms" ? <Loader2 size={14} className="animate-spin mx-auto" /> : "Send code"}
                      </button>
                    )}
                    {!otpVerified && !otpSent && otpWhatsappEnabled && (
                      <button
                        type="button"
                        onClick={() => sendCode("whatsapp")}
                        disabled={!form.phone || otpSending || cooldown > 0}
                        data-testid="enquiry-otp-send-whatsapp"
                        title="Send OTP on WhatsApp"
                        className="shrink-0 rounded-lg border border-emerald text-emerald px-3 whitespace-nowrap hover:bg-emerald/5 transition-colors disabled:opacity-50 disabled:hover:bg-transparent"
                      >
                        {otpSending && otpChannel === "whatsapp" ? <Loader2 size={14} className="animate-spin mx-auto" /> : <MessageCircle size={16} />}
                      </button>
                    )}
                    {!otpVerified && otpSent && (
                      <button
                        type="button"
                        onClick={() => sendCode(otpChannel)}
                        disabled={!form.phone || otpSending || cooldown > 0}
                        data-testid="enquiry-otp-resend"
                        className="shrink-0 rounded-lg border border-emerald text-emerald font-semibold text-xs px-3 whitespace-nowrap hover:bg-emerald/5 transition-colors disabled:opacity-50 disabled:hover:bg-transparent"
                      >
                        {otpSending ? (
                          <Loader2 size={14} className="animate-spin mx-auto" />
                        ) : cooldown > 0 ? (
                          `Resend (${cooldown}s)`
                        ) : (
                          "Resend code"
                        )}
                      </button>
                    )}
                    {otpVerified && (
                      <span
                        data-testid="enquiry-otp-verified"
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-emerald/10 text-emerald font-semibold text-xs px-3"
                      >
                        <ShieldCheck size={14} /> Verified
                      </span>
                    )}
                  </div>
                  {otpSent && !otpVerified && (
                    <p className="font-sans text-[11px] text-graphite mt-1.5">
                      Code sent via {otpChannel === "whatsapp" ? "WhatsApp" : "SMS"}.{" "}
                      {otpChannel === "sms" && otpWhatsappEnabled && cooldown === 0 && (
                        <button type="button" onClick={() => sendCode("whatsapp")} className="text-emerald font-bold underline underline-offset-2">
                          Try WhatsApp instead
                        </button>
                      )}
                      {otpChannel === "whatsapp" && otpEnabled && cooldown === 0 && (
                        <button type="button" onClick={() => sendCode("sms")} className="text-emerald font-bold underline underline-offset-2">
                          Try SMS instead
                        </button>
                      )}
                    </p>
                  )}
                  {phoneError && (
                    <p data-testid="enquiry-phone-error" className="text-sm text-destructive font-mono mt-2">
                      {phoneError}
                    </p>
                  )}
                </Field>
              </div>

              {(otpEnabled || otpWhatsappEnabled) && otpSent && !otpVerified && (
                <Field label="Verification code" testId="enquiry-otp-code">
                  <div className="flex gap-2">
                    <input
                      required
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                      inputMode="numeric"
                      pattern="[0-9]{4,8}"
                      minLength={4}
                      maxLength={8}
                      data-testid="enquiry-otp-code-input"
                      className="w-full bg-white text-ink rounded-lg border border-line px-4 py-3 outline-none focus:border-emerald focus:ring-1 focus:ring-emerald transition-colors tracking-[0.3em]"
                      placeholder="6-digit code"
                    />
                    <button
                      type="button"
                      onClick={verifyCode}
                      disabled={!OTP_PATTERN.test(otpCode) || otpVerifying}
                      data-testid="enquiry-otp-verify"
                      className="shrink-0 rounded-lg bg-emerald text-white font-semibold text-sm px-5 hover:bg-emeraldhover transition-colors disabled:opacity-50"
                    >
                      {otpVerifying ? <Loader2 size={16} className="animate-spin" /> : "Verify"}
                    </button>
                  </div>
                  {otpError && (
                    <p data-testid="enquiry-otp-error" className="text-sm text-destructive font-mono mt-2">
                      {otpError}
                    </p>
                  )}
                </Field>
              )}

              <Field label="Anything you'd like us to know (optional)" testId="enquiry-message">
                <textarea
                  rows={3}
                  value={form.message}
                  onChange={upd("message")}
                  data-testid="enquiry-message-input"
                  className="w-full bg-white text-ink rounded-lg border border-line px-4 py-3 outline-none focus:border-emerald focus:ring-1 focus:ring-emerald transition-colors"
                  placeholder="Your background, timing, questions…"
                />
              </Field>

              {error && (
                <p data-testid="enquiry-error" className="text-sm text-destructive font-mono">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="enquiry-submit"
                className="w-full rounded-full bg-terracotta text-white font-semibold text-base px-8 py-4 shadow-md hover:bg-terracottahover hover:scale-[1.01] transition duration-300 disabled:opacity-50 disabled:hover:scale-100"
              >
                {submitting ? "Sending…" : "Send Enquiry"}
              </button>
              <p className="font-mono text-[10px] uppercase tracking-widest text-graphite text-center">
                {otpRequired && !otpVerified
                  ? "Verify your phone number above to continue"
                  : "No payment taken here · we confirm details with you directly"}
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="font-mono text-[10px] uppercase tracking-widest text-graphite mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}

function RadioCard({ label, selected, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${
        selected ? "border-emerald bg-emerald/5" : "border-line hover:border-emerald/50"
      }`}
    >
      <span className="text-[14px] font-semibold text-ink">{label}</span>
      <span
        className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
          selected ? "border-emerald" : "border-line"
        }`}
      >
        {selected && <span className="w-2.5 h-2.5 rounded-full bg-emerald" />}
      </span>
    </button>
  );
}
